<script setup>
import { getCurrentInstance, reactive, ref, watch } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import _ from "lodash";
import axios from "axios";
import CompanyPreview from "./companyPreview.vue";
import FileUpload from "./fileUpload.vue";
import ImageUpload from "./imageUpload.vue";
import {
  queryCompanyInfo,
  insertCompanyInfo,
  updateCompanyInfo,
  queryCompanyStock,
  queryCompanyCertificates,
  queryCompanyContracts,
  insertCompanyStock,
  updateCompanyStock,
  deleteCompanyStock,
  insertCompanyCertificate,
  updateCompanyCertificate,
  deleteCompanyCertificate,
  queryStationListByDeptId,
  insertCompanyContract,
  updateCompanyContract,
  deleteCompanyContract,
  queryOrgTree,
} from "@/api/companyBaseInfo";
import { fileUploadApi, previewFileApi } from "@/api/common";
import { adjustFlowChart, deleteFlowChart } from "@/api/chart";
import { parseTime } from "@/utils/common";

const props = defineProps({
  params: {
    type: Object,
    required: false,
    default: null,
  },
});

// 组件实例
const { proxy } = getCurrentInstance();

// 全屏遮罩
const fullscreenLoading = ref(false);

// 表单数据
const formData = ref({
  id: undefined,
  deptId: undefined, //公司Id
  companyName: undefined, //公司名称
  companyCategory: undefined, //公司类别
  legalPersonName: undefined, //法人姓名
  dateOfEstablishment: undefined, //成立日期
  registeredCapital: undefined, //注册资金
  companyRegisteredAddress: undefined, //注册地址
  servicePopulation: undefined, //服务人口
  employeeNumber: undefined, //员工人数
  companyLongitude: undefined, //公司经度
  companyDimension: undefined, //公司纬度
  businessScope: undefined, //经营范围
  photo: [], //公司宣传图
  serviceArea: undefined, //服务面积
  ifStation: 0, //是否区域人员常驻地
  promotionalImageIdList: [], //公司宣传图id
  promotionalImageUrlList: [],
  networkLength: undefined, //主管网长度
});

// 合同表单数据
const contractFormData = ref({
  name: undefined, //合同名称
  businessModel: undefined, //商业模式
  startAndEnd: [], //合同起止日期
  firstParty: undefined, //合同甲方
  cooperativeUnit: undefined, //合作单位
  yearsRemaining: undefined, //剩余年限
  depts: [], //关联厂站
  fileInfo: [], //合同文件
  fileIdList: [], //合同文件id
  contractFilesObj: [], //已存在合同文件
});

// 证件表单
const documentsFormData = ref({
  certificateName: undefined, //证件名称
  certificateInfoList: [], //证件文件
  certificateIdList: [], //已存在的证件文件id
  filesObj: [], // 已有的证件文件对象
});

// 自定义校验规则
const fileInfoMustUpload = (rule, value, callback) => {
  if (value.length === 0 && contractFormData.value.fileIdList.length === 0) {
    // 未上传文件
    callback("合同文件不能为空");
  }
  callback();
};

const fileMustUpload = (rule, value, callback) => {
  if (
    value.length === 0 &&
    documentsFormData.value.certificateIdList.length === 0
  ) {
    // 未上传文件
    callback("证件文件不能为空");
  }
  callback();
};
const imageMustUpload = (rule, value, callback) => {
  if (
    value.length === 0 &&
    formData.value.promotionalImageIdList.length === 0
  ) {
    // 未上传文件
    callback("宣传图片不能为空");
  }
  callback();
};
//整数或者小数验证
const validateNum = (rule, value, callback) => {
  let boolean = new RegExp("^[0-9]*$").test(value); //整数
  let float = new RegExp("^([0-9]{1,}[.][0-9]*)$").test(value); //小数
  if (!boolean && !float) {
    callback(new Error("只能输入数字"));
  } else {
    callback();
  }
};

// 基础信息表单校验
const formRules = reactive({
  companyName: [
    { required: true, message: "公司名称不能为空", trigger: "blur" },
  ],
  companyCategory: [
    { required: true, message: "公司类别不能为空", trigger: "change" },
  ],
  legalPersonName: [
    { required: true, message: "法人姓名不能为空", trigger: "blur" },
  ],
  dateOfEstablishment: [
    { required: true, message: "成立日期不能为空", trigger: "blur" },
  ],
  registeredCapital: [
    { required: true, message: "注册资金不能为空", trigger: "blur" },
    { validator: validateNum, trigger: "blur" },
  ],
  companyRegisteredAddress: [
    { required: true, message: "注册地址不能为空", trigger: "blur" },
  ],
  servicePopulation: [
    { required: true, message: "服务人口不能为空", trigger: "blur" },
    { validator: validateNum, trigger: "blur" },
  ],
  employeeNumber: [
    { required: true, message: "员工人数不能为空", trigger: "blur" },
    { validator: validateNum, trigger: "blur" },
  ],
  companyLongitude: [
    {
      required: true,
      message: "公司经度不能为空",
      trigger: "blur",
    },
    {
      pattern:
        /^(|\+)?(((\d|[1-9]\d|1[0-7]\d|0{1,3})\.\d{0,8})|(\d|[1-9]\d|1[0-7]\d|0{1,3})|180\.0{0,8}|180)$/,
      message: "经度整数部分为0-180,小数部分为0到8位!",
      trigger: "blur",
    },
  ],
  companyDimension: [
    {
      required: true,
      message: "公司纬度不能为空",
      trigger: "blur",
    },
    {
      pattern: /^(|\+)?([0-8]?\d\.\d{0,8}|90\.0{0,8}|[0-8]?\d|90)$/,
      message: "纬度整数部分为0-90,小数部分为0到8位!",
      trigger: "blur",
    },
  ],
  businessScope: [
    { required: true, message: "经营范围不能为空", trigger: "blur" },
  ],
  photo: [{ required: true, validator: imageMustUpload, trigger: "change" }],
  serviceArea: [
    { required: true, message: "服务面积不能为空", trigger: "blur" },
    { validator: validateNum, trigger: "blur" },
  ],
  ifStation: [
    { required: true, message: "是否区域人员常驻地不能为空", trigger: "blur" },
  ],
  networkLength: [
    { required: true, message: "主管网长度不能为空", trigger: "blur" },
    { validator: validateNum, trigger: "blur" },
  ],
});

// 股权表单
const equityFormData = reactive({
  id: undefined,
  shareholderName: undefined, //股东名称
  shareProportion: undefined, //占股比例
});

// 股权表单校验
const equityFormRules = reactive({
  shareholderName: [
    { required: true, message: "股东名称不能为空", trigger: "blur" },
  ],
  shareProportion: [
    { required: true, message: "占股比例不能为空", trigger: "blur" },
  ],
});

// 证件表单校验
const documentsFormRules = reactive({
  certificateName: [
    { required: true, message: "证件名称不能为空", trigger: "blur" },
  ],
  certificateInfoList: [
    { required: true, validator: fileMustUpload, trigger: "change" },
  ],
});

// 合同表单校验
const contractFormRules = reactive({
  name: [{ required: true, message: "合同名称不能为空", trigger: "blur" }],
  businessModel: [
    { required: true, message: "商业模式不能为空", trigger: "blur" },
  ],
  startAndEnd: [
    { required: true, message: "合同起止日期不能为空", trigger: "blur" },
  ],
  firstParty: [
    { required: true, message: "合同甲方不能为空", trigger: "blur" },
  ],
  cooperativeUnit: [
    { required: true, message: "合作单位不能为空", trigger: "blur" },
  ],
  depts: [{ required: true, message: "关联厂站不能为空", trigger: "change" }],
  fileInfo: [
    { required: true, validator: fileInfoMustUpload, trigger: "change" },
  ],
});

//组织架构
const organizationFormData = reactive({
  parentId: undefined,
  lable1: undefined, //组织名称
  lable2: undefined, //负责人
  lable3: undefined, //组织人数
});

// 组织架构表单校验
const organizationFormRules = reactive({
  lable1: [{ required: true, message: "组织名称不能为空", trigger: "blur" }],
});

// 组织架构弹窗控制
const openDialogOrganization = ref(false);

// 图片上传组件按钮是否显示
const imgBtnShow = ref(true);

// 提交按钮统一loading
const submitLoading = ref(false);

// 表格统一loading
const tableLoading = ref(false);

//股权列表
const equityDataList = ref([]);

//证件列表
const documentsDataList = ref([]);

//合同列表
const contractDataList = ref([]);

// 股权弹窗控制
const openDialogEquity = ref(false);

// 证件弹窗控制
const openDialogDocuments = ref(false);

// 合同弹窗控制
const openDialogContract = ref(false);

// 弹窗统一title
const dialogTitle = ref("新增");

// 公司信息是否已经填写
const isInfoFill = ref(false);

// 证件文件是否发生改变
const isCertificateChanged = ref(false);

// 合同文件是否变化
const isContractChange = ref(false);

// 组织架构
const treeData = ref({
  parentId: undefined,
  lable1: "根节点",
  lable2: undefined,
  lable3: undefined,
});

// 组织架构整棵树
const fullTreeData = ref([]);

// 组织架构props
const treeOrgProps = {
  label: "lable1",
  pid: "parentId",
};

// 组织架构组件工具栏
const toolBarProps = reactive({
  scale: false,
  restore: false,
  expand: false,
  zoom: false,
  fullscreen: false,
});

//厂站列表
const factoryStationList = ref([]);

// 股权分页
const equityPagination = reactive({
  pageNum: 1,
  pageSize: 10,
  total: 0,
});

// 证件分页
const documentsPagination = reactive({
  pageNum: 1,
  pageSize: 10,
  total: 0,
});

// 合同分页
const contractPagination = reactive({
  pageNum: 1,
  pageSize: 10,
  total: 0,
});

//table序号 分页问题
const equityTabCount = (index) => {
  return (equityPagination.pageNum - 1) * equityPagination.pageSize + index + 1;
};

//table序号 分页问题
const documentsTabCount = (index) => {
  return (
    (documentsPagination.pageNum - 1) * documentsPagination.pageSize + index + 1
  );
};
//table序号 分页问题
const contractTabCount = (index) => {
  return (
    (contractPagination.pageNum - 1) * contractPagination.pageSize + index + 1
  );
};

//查询证件
const getCertificateList = async () => {
  const params = {
    pageNum: documentsPagination.pageNum,
    pageSize: documentsPagination.pageSize,
    deptId: props.params.id,
  };

  const result = await queryCompanyCertificates(params);

  if (result.data.records.length === 0 && documentsPagination.pageNum > 1) {
    --documentsPagination.pageNum;
    await getCertificateList();
    return;
  }
  documentsDataList.value = result.data.records;
  documentsPagination.total = result.data.total;
};

//查询组织架构
const getChartTreeList = async () => {
  try {
    const result = await queryOrgTree(props.params.id);
    if (result.data.length > 0) {
      treeData.value = result.data[0];
      fullTreeData.value = result.data;
    } else {
      treeData.value = {
        id: 0,
        lable1: "根节点",
        children: [],
      };
      fullTreeData.value = [];
    }
  } catch (e) {
    //
  }
};

//查询股权架构
const getStructureList = async () => {
  const params = {
    pageNum: equityPagination.pageNum,
    pageSize: equityPagination.pageSize,
    deptId: props.params.id,
  };
  const result = await queryCompanyStock(params);
  if (result.data.records.length === 0 && equityPagination.pageNum > 1) {
    --equityPagination.pageNum;
    return await getStructureList();
  }
  equityDataList.value = result.data.records;
  equityPagination.total = result.data.total;
};

//查询合同列表
const getContractDataList = async () => {
  const params = {
    pageNum: contractPagination.pageNum,
    pageSize: contractPagination.pageSize,
    deptId: props.params.id,
  };
  const result = await queryCompanyContracts(params);
  if (result.data.records.length === 0 && contractPagination.pageNum > 1) {
    --contractPagination.pageNum;
    await getContractDataList();
    return;
  }
  contractDataList.value = result.data.records;
  contractPagination.total = result.data.total;
};

//查询基本信息
const getCompanyBaseList = async () => {
  try {
    const result = await queryCompanyInfo(props.params.id);
    isInfoFill.value = JSON.stringify(result.data) != "{}";
    if (result.data) {
      formData.value = result.data;
      formData.value.photo = [];
      formData.value.promotionalImageIdList = [];
      formData.value.promotionalImageUrlList = [];
    }
  } catch (e) {
    console.error(e);
  }
};

//证件文件改变回调
const fileChange = (newFile, oldFile) => {
  documentsFormData.value.certificateInfoList = newFile;
  documentsFormData.value.certificateIdList = oldFile.map((m) => m.id);
  if (newFile.length > 0) {
    isCertificateChanged.value = true;
    proxy.$refs.documentsFormRef &&
      proxy.$refs.documentsFormRef.clearValidate(["certificateInfoList"]);
  } else {
    isCertificateChanged.value = false;
    proxy.$refs.documentsFormRef &&
      proxy.$refs.documentsFormRef.validateField("certificateInfoList");
  }
};

//合同文件改变回调
const contractFileChange = (newFile, oldFile) => {
  contractFormData.value.fileInfo = newFile;
  contractFormData.value.fileIdList = oldFile.map((m) => m.id);
  if (newFile.length > 0) {
    isContractChange.value = true;
    proxy.$refs.contractFormRef &&
      proxy.$refs.contractFormRef.clearValidate(["fileInfo"]);
  } else {
    isContractChange.value = false;
    proxy.$refs.contractFormRef &&
      proxy.$refs.contractFormRef.validateField("fileInfo");
  }
};

//组织架构弹窗取消
const handleOrganizationFormReset = () => {
  organizationFormData.parentId = undefined;
  organizationFormData.lable1 = undefined;
  organizationFormData.lable2 = undefined;
  organizationFormData.lable3 = undefined;

  openDialogOrganization.value = false;
};

//组织架构表单提交
const handleOrganizationFormSubmit = () => {
  proxy.$refs.organizationFormRef.validate((valid) => {
    if (valid) {
      submitLoading.value = true;
      const oldOrgList = TreeArrayPlanarity(fullTreeData.value);
      if (dialogTitle.value === "新增") {
        oldOrgList.push({
          deptId: props.params.id,
          ...organizationFormData,
          type: "1",
        });
      } else {
        // 将旧数据替换为新数据
        for (let i = 0; i < oldOrgList.length; i++) {
          if (oldOrgList[i].id == organizationFormData.id) {
            oldOrgList[i] = organizationFormData;
            oldOrgList[i].type = "1";
            break;
          }
        }
      }
      adjustFlowChart({
        deptId: props.params.id,
        reqs: oldOrgList,
      })
        .then((res) => {
          if (res.code == "2000") {
            ElMessage.success("操作成功");
            openDialogOrganization.value = false;
            getChartTreeList();
          }
        })
        .finally(() => {
          submitLoading.value = false;
          organizationFormData.lable1 = null;
          organizationFormData.lable2 = null;
          organizationFormData.lable3 = null;
        });
    }
  });
};

// 点击保存
const handleFormSubmit = () => {
  proxy.$refs.saveFormRef?.validate((valid) => {
    if (valid) {
      if (isInfoFill.value) {
        // 编辑
        updateCompanyInfo(formData.value).then((res) => {
          if (res.code == "2000") {
            ElMessage.success("操作成功");
          }
        });
      } else {
        // 新增
        insertCompanyInfo(formData.value).then((res) => {
          if (res.code == "2000") {
            ElMessage.success("新增成功");
          }
        });
      }
    }
  });
};

// 点击新增股权架构
const addNewEquity = () => {
  dialogTitle.value = "新增";
  openDialogEquity.value = true;
  equityFormData.shareholderName = undefined;
  equityFormData.shareProportion = undefined;
  equityFormData.id = undefined;
};

// 点击编辑股权架构
const handleEquityUpdate = (row) => {
  dialogTitle.value = "编辑";
  openDialogEquity.value = true;
  equityFormData.shareholderName = row.shareholderName;
  equityFormData.shareProportion = row.shareProportion;
  equityFormData.id = row.id;
};

// 点击删除股权架构
const handleEquityDelete = (row) => {
  ElMessageBox.confirm("确定删除此股东吗", "警告", {
    confirmButtonText: "确定",
    cancelButtonText: "取消",
    type: "error",
  })
    .then(() => {
      deleteCompanyStock(row.id).then((res) => {
        if (res.code == "2000") {
          ElMessage.success("删除成功");
          getStructureList();
        }
      });
    })
    .catch(() => {});
};

// 点击保存股权架构
const handleEquityFormSubmit = () => {
  submitLoading.value = true;
  proxy.$refs.equityFormRef?.validate((valid) => {
    if (valid) {
      if (equityFormData.id) {
        updateCompanyStock({ ...equityFormData, deptId: props.params.id }).then(
          (res) => {
            if (res.code == "2000") {
              ElMessage.success("操作成功");
              openDialogEquity.value = false;
              submitLoading.value = false;
              getStructureList();
            }
          }
        );
      } else {
        insertCompanyStock({ ...equityFormData, deptId: props.params.id }).then(
          (res) => {
            if (res.code == "2000") {
              ElMessage.success("操作成功");
              openDialogEquity.value = false;
              submitLoading.value = false;
              getStructureList();
            }
          }
        );
      }
    } else {
      submitLoading.value = false;
    }
  });
};

// 点击新增相关证件
const addNewDocuments = () => {
  dialogTitle.value = "新增";
  openDialogDocuments.value = true;
  documentsFormData.value = {
    certificateName: undefined, //证件名称
    certificateInfoList: [], //证件文件
    certificateIdList: [], //已存在的证件文件id
    filesObj: [], // 已有的证件文件对象
  };
};

// 点击保存相关证件
const handleDocumentsFormSubmit = () => {
  submitLoading.value = true;
  proxy.$refs.documentsFormRef?.validate((valid) => {
    if (valid) {
      const fileData = new FormData();
      documentsFormData.value.certificateInfoList.forEach((file) => {
        fileData.append("file", file);
      });
      // 文件变化和没有变化两种情况
      if (documentsFormData.value.id) {
        // 编辑
        // 文件变化
        if (isCertificateChanged.value) {
          fileUploadApi(fileData).then((res) => {
            if (res.code == "2000") {
              updateCompanyCertificate({
                id: documentsFormData.value.id,
                deptId: props.params.id,
                fileUrl: res.data.reduceUrl,
                name: documentsFormData.value.certificateName,
                fileName: res.data.originalName,
              }).then((response) => {
                if (response.code == "2000") {
                  ElMessage.success("操作成功");
                  submitLoading.value = false;
                  openDialogDocuments.value = false;
                  getCertificateList();
                }
              });
            }
          });
          return;
        }
        // 文件没有发生变化的情况
        updateCompanyCertificate({
          id: documentsFormData.value.id,
          name: documentsFormData.value.certificateName,
          deptId: props.params.id,
        }).then((res) => {
          if (response.code == "2000") {
            ElMessage.success("操作成功");
            submitLoading.value = false;
            openDialogDocuments.value = false;
            getCertificateList();
          }
        });
      } else {
        // 新增
        fileUploadApi(fileData).then((res) => {
          if (res.code == "2000") {
            // 将文件下载路径传给后端进行保存
            insertCompanyCertificate({
              id: undefined,
              deptId: props.params.id,
              fileUrl: res.data.reduceUrl,
              name: documentsFormData.value.certificateName,
              fileName: res.data.originalName,
            }).then((response) => {
              if (response.code == "2000") {
                ElMessage.success("新增成功");
                submitLoading.value = false;
                openDialogDocuments.value = false;
                getCertificateList();
              }
            });
          }
        });
      }
    } else {
      submitLoading.value = false;
    }
  });
};

// 证件点击下载
const certificateDownload = (row) => {
  axios
    .post(
      "/api/operate/file/download",
      {
        reduceUrl: row.fileUrl,
      },
      { responseType: "blob" }
    )
    .then((res) => {
      const reader = new FileReader();
      reader.readAsDataURL(res.data); // 转换为base64，可以直接放入a标签href
      reader.onload = (e) => {
        // 转换完成，创建一个a标签用于下载
        let a = document.createElement("a");
        a.style.display = "none";
        a.download = row.fileName;
        a.href = e.target.result;
        document.body.appendChild(a); // 修复firefox中无法触发click
        a.click();
        document.body.removeChild(a);
      };
    });
};

// 证件点击编辑
const handleDocumentsUpdate = (row) => {
  documentsFormData.value.id = row.id;
  dialogTitle.value = "编辑";
  documentsFormData.value.certificateName = row.name;
  documentsFormData.value.filesObj = [
    {
      id: row.id,
      name: row.fileName,
    },
  ];
  openDialogDocuments.value = true;
};

// 证件点击删除
const handleDocumentsDelete = (row) => {
  ElMessageBox.confirm("确定删除此证件吗", "警告", {
    confirmButtonText: "确定",
    cancelButtonText: "取消",
    type: "error",
  })
    .then(() => {
      deleteCompanyCertificate(row.id).then((res) => {
        if (res.code == "2000") {
          ElMessage.success("删除成功");
          getCertificateList();
        }
      });
    })
    .catch(() => {});
};

// 点击新增合同
const addNewContract = () => {
  dialogTitle.value = "新增";
  contractFormData.value = {
    name: undefined, //合同名称
    businessModel: undefined, //商业模式
    startAndEnd: [], //合同起止日期
    firstParty: undefined, //合同甲方
    cooperativeUnit: undefined, //合作单位
    yearsRemaining: undefined, //剩余年限
    depts: [], //关联厂站
    fileInfo: [], //合同文件
    fileIdList: [], //合同文件id
    contractFilesObj: [], //已存在合同文件
  };
  openDialogContract.value = true;
  getFactoryStationList();
};

// 合同点击保存
const handleContractFormSubmit = () => {
  submitLoading.value = true;
  proxy.$refs.contractFormRef?.validate((valid) => {
    if (valid) {
      const fileData = new FormData();
      contractFormData.value.fileInfo.forEach((file) => {
        fileData.append("file", file);
      });
      if (contractFormData.value.id) {
        // 编辑
        if (isContractChange.value) {
          fileUploadApi(fileData).then((res) => {
            if (res.code == "2000") {
              updateCompanyContract({
                ...contractFormData.value,
                deptId: props.params.id,
                plants: contractFormData.value.depts.join(","),
                files: res.data.reduceUrl,
                fileName: res.data.originalName,
              }).then((response) => {
                if (response.code == "2000") {
                  ElMessage.success("操作成功");
                  submitLoading.value = false;
                  openDialogContract.value = false;
                  getContractDataList();
                }
              });
            }
          });
          return;
        }
        updateCompanyContract({
          ...contractFormData.value,
          deptId: props.params.id,
          plants: contractFormData.value.depts.join(","),
        }).then((response) => {
          if (response.code == "2000") {
            ElMessage.success("操作成功");
            submitLoading.value = false;
            openDialogContract.value = false;
            getContractDataList();
          }
        });
      } else {
        // 新增
        fileUploadApi(fileData).then((res) => {
          if (res.code == "2000") {
            insertCompanyContract({
              ...contractFormData.value,
              deptId: props.params.id,
              plants: contractFormData.value.depts.join(","),
              files: res.data.reduceUrl,
              fileName: res.data.originalName,
            }).then((response) => {
              if (response.code == "2000") {
                ElMessage.success("新增成功");
                submitLoading.value = false;
                openDialogContract.value = false;
                getContractDataList();
              }
            });
          }
        });
      }
    } else {
      submitLoading.value = false;
    }
  });
};

// 获取公司下的厂站列表
const getFactoryStationList = () => {
  queryStationListByDeptId(props.params.id).then((res) => {
    factoryStationList.value = res.data || [];
  });
};

// 合同开始结束时间变化
const startAndEndChange = (val) => {
  contractFormData.value.effectiveStartTime = val ? val[0] : null;
  contractFormData.value.effectiveEndTime = val ? val[1] : null;
};

// 点击剩余年限
const setCompareRegistrationTimeText = (startDate, endDate) => {
  const date = new Date(startDate); // 转换时间格式
  const year = date.getFullYear(); // 取当年的年
  const month = date.getMonth() + 1; // 取当年的月(月份加一)
  const dd = date.getDate(); // 取当年的日期
  const nowDate = new Date(endDate); // 取现在的时间
  const nowYear = nowDate.getFullYear(); // 取现在的年
  const nowMonth = nowDate.getMonth() + 1; // 取现在的月(月份加一)
  const nowDd = nowDate.getDate(); // 取现在的日期
  /**
   * 日期计算(结束 - 开始)
   * 1-1当差值为正，就相差多少天
   * 1-1-1例如: (2021/3/15 - 2022/4/18) ===== 18-15 > 0 日期相差3天
   * 1-2当差值为负，计算从开始时间的日期到结束时间的日期相差几天
   * 1-2-1例如：(2021/3/15 - 2022/4/10) ===== 10-15 < 0
   * 其实就是计算从3/15 到 4、10号中间过了多少天
   * 1-2-1-1：方法： 其实就是计算3/15 到 3/31 号过了多少天，再加上 4月过的10天
   */
  const restDd =
    nowDd - dd < 0 ? lastDay(nowMonth - 1, year) - dd + nowDd : nowDd - dd;
  /**
   * 月份计算(结束 - 开始)
   * 1-1当差值为正，就相差多少月
   * 1-1-1例如: (2021/3/15 - 2022/4/18) ===== 4-3 > 0 月份相差1月
   * 1-2当差值为负，计算从开始时间的月份到结束时间的月份相差几天
   * 1-2-1例如：(2021/5/15 - 2022/4/10) ===== 4-5 < 0
   * 其实就是计算从5月到第二年4月过了多少月
   * 1-2-1-1：方法： 向年借一年为12月计算过了多少月
   */
  const restMonth =
    nowMonth - month < 0 ? 12 + nowMonth - month : nowMonth - month;
  /**
   * 年份计算(结束 - 开始)
   * 直接限制结束比开始大，只有0/正数
   */
  const restYear = nowYear - year;
  /**
   * 计算借位的问题
   */
  let resultMonth = restMonth;
  let resultYear = restYear;
  // 日期小说明借了月
  if (nowDd < dd) {
    resultMonth = restMonth - 1 < 0 ? restMonth - 1 + 12 : restMonth - 1;
  }
  // 月份小借了年 或者 日期小，月份刚好一致，因为日期借了月份，导致月份减1，所以减年
  if (nowMonth < month || (nowDd < dd && nowMonth === month)) {
    resultYear = restYear - 1;
  }
  // let str = {
  //     year: resultYear,
  //     month: resultMonth,
  //     day: restDd,
  // }
  return `${resultYear}年${resultMonth}月`;
};

/**
 * 判断每年的每个月的最后一天是几号
 * @param mo-月份
 * @param year-年份
 * @returns {number}
 */
const lastDay = (mo, year) => {
  if (mo === 4 || mo === 6 || mo === 9 || mo === 11) {
    return 30;
  }
  //2月
  else if (mo === 2) {
    if (this.isLeapYear(year)) {
      return 29;
    } else {
      return 28;
    }
  }
  //大月
  else {
    return 31;
  }
};

// 点击编辑合同
const handleContractUpdate = (row) => {
  dialogTitle.value = "编辑";
  getFactoryStationList();
  contractFormData.value = { ...row };
  contractFormData.value.depts = row.plants.split(",");
  contractFormData.value.startAndEnd = [
    row.effectiveStartTime,
    row.effectiveEndTime,
  ];
  contractFormData.value.contractFilesObj = [
    {
      id: row.id,
      name: row.fileName,
    },
  ];
  contractFormData.value.fileIdList = [
    {
      id: row.id,
      name: row.fileName,
    },
  ];
  openDialogContract.value = true;
};

// 点击删除合同
const handleContractDelete = (row) => {
  ElMessageBox.confirm("确定删除此合同吗", "警告", {
    confirmButtonText: "确定",
    cancelButtonText: "取消",
    type: "error",
  })
    .then(() => {
      deleteCompanyContract(row.id).then((res) => {
        if (res.code == "2000") {
          ElMessage.success("删除成功");
          getContractDataList();
        }
      });
    })
    .catch(() => {});
};

// 公司卡预览
const previewOpen = () => {
  proxy.$refs.companyPreviewRef?.handleDialogOpen();
};

// 公司宣传图变更
const promotionalImageChange = (file) => {
  formData.value.promotionalImage = file.reduceUrl;
};

// 新增组织架构
const addClick = (data) => {
  organizationFormData.parentId = data.id;
  dialogTitle.value = "新增";
  openDialogOrganization.value = true;
};

// 编辑组织架构
const editClick = (data) => {
  dialogTitle.value = "编辑";
  const keys = ["id", "lable1", "lable2", "lable3", "parentId"];
  keys.forEach((key) => {
    organizationFormData[key] = data.$$data[key];
  });
  if (!data.$$data.lable3) organizationFormData.lable3 = undefined;
  openDialogOrganization.value = true;
};

// 将组织树平铺
const TreeArrayPlanarity = (tree, flattenedArray = []) => {
  for (let i = 0; i < tree.length; i++) {
    flattenedArray.push({
      id: tree[i].id,
      lable1: tree[i].lable1,
      lable2: tree[i].lable2,
      lable3: tree[i].lable3,
      parentId: tree[i].parentId,
      type: "1",
    });
    if (tree[i].children && tree[i].children.length > 0) {
      TreeArrayPlanarity(tree[i].children, flattenedArray);
    }
  }
  return flattenedArray;
};

const deleteClick = (data) => {
  if (!data.children) {
    ElMessageBox.confirm("是否确认删除数据项?", "警告", {
      confirmButtonText: "确定",
      cancelButtonText: "取消",
      type: "warning",
    }).then(() => {
      deleteFlowChart(data.id).then((res) => {
        if (res.code == "2000") {
          ElMessage.success("删除成功");
          getChartTreeList();
        }
      });
    });
  } else {
    ElMessage.warning("不能删除父级");
  }
};

watch(
  [() => props.params, contractFormData.value],
  ([newParams], [newContract]) => {
    if (newParams) {
      formData.value.companyName = newParams.name;
      formData.value.deptId = newParams.id;
      getCompanyBaseList();
      getStructureList();
      getCertificateList();
      getContractDataList();
      getChartTreeList();
    }

    if (newContract) {
      if (
        Array.isArray(newContract.startAndEnd) &&
        newContract.startAndEnd.length > 0
      ) {
        contractFormData.value.yearsRemaining = setCompareRegistrationTimeText(
          parseTime(new Date(), "{y}-{m}-{d}"),
          val.startAndEnd[1]
        );
      } else {
        contractFormData.value.yearsRemaining = "";
      }
    }
  },
  {
    deep: true,
    immediate: true,
  }
);
</script>

<template>
  <div
    v-loading.fullscreen.lock="fullscreenLoading"
    element-loading-background="rgba(0, 0, 0, 0.6)"
  >
    <el-card class="box-card" shadow="never">
      <template #header>
        <div class="my_line_box">
          <el-divider direction="vertical" class="my_line"></el-divider>
          <span class="my_line_txt">基本信息</span>
        </div>
        <el-button
          type="primary"
          size="small"
          plain
          icon="View"
          @click="previewOpen"
          >预览</el-button
        >
      </template>
      <el-form
        ref="saveFormRef"
        :model="formData"
        :rules="formRules"
        id="saveFormRef"
        label-position="top"
      >
        <el-row :gutter="10">
          <el-col :span="8">
            <el-form-item label="公司名称" prop="companyName" class="mb10">
              <el-input
                v-model.trim="formData.companyName"
                name="companyName"
                placeholder="请输入公司名称"
                disabled
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="公司类别" prop="companyCategory">
              <el-select
                v-model.trim="formData.companyCategory"
                placeholder="请选择公司类别"
                style="width: 100%"
              >
                <el-option label="供水" value="1"></el-option>
                <el-option label="排水" value="2"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="法人姓名" prop="legalPersonName">
              <el-input
                v-model.trim="formData.legalPersonName"
                name="legalPersonName"
                placeholder="请输入法人姓名"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="10">
          <el-col :span="8">
            <el-form-item label="注册资金" prop="registeredCapital">
              <el-input
                v-model.trim="formData.registeredCapital"
                name="registeredCapital"
                placeholder="请输入注册资金"
              >
                <template #append>万元</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="成立日期" prop="dateOfEstablishment">
              <el-date-picker
                v-model="formData.dateOfEstablishment"
                value-format="YYYY-MM-DD HH:mm:ss"
                format="YYYY-MM-DD HH:mm:ss"
                type="date"
                placeholder="请选择成立日期"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="员工人数" prop="employeeNumber">
              <el-input
                v-model.trim="formData.employeeNumber"
                name="employeeNumber"
                placeholder="请输入员工人数"
              >
                <template #append>人</template>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="10">
          <el-col :span="8">
            <el-form-item label="服务面积" prop="serviceArea">
              <el-input
                v-model.trim="formData.serviceArea"
                name="serviceArea"
                placeholder="请输入服务面积"
              >
                <template #append>m²</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="服务人口" prop="servicePopulation">
              <el-input
                v-model.trim="formData.servicePopulation"
                name="servicePopulation"
                placeholder="请输入服务人口"
              >
                <template #append>万人</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item
              label="主管网长度"
              prop="networkLength"
              v-if="formData.companyCategory == 1"
            >
              <el-input
                v-model.trim="formData.networkLength"
                name="networkLength"
                placeholder="请输入主管网长度"
              >
                <template #append>km</template>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="10">
          <el-col :span="24">
            <el-form-item label="注册地址" prop="companyRegisteredAddress">
              <el-input
                v-model.trim="formData.companyRegisteredAddress"
                name="companyRegisteredAddress"
                placeholder="请输入注册地址"
                style="width: 96.7%"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="10">
          <el-col :span="8">
            <el-form-item label="公司经度" prop="companyLongitude">
              <el-input
                v-model.trim="formData.companyLongitude"
                name="companyLongitude"
                placeholder="请输入公司经度"
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="公司纬度" prop="companyDimension">
              <el-input
                v-model.trim="formData.companyDimension"
                name="companyDimension"
                placeholder="请输入公司纬度"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="10">
          <el-col :span="24">
            <el-form-item label="经营范围" prop="businessScope">
              <el-input
                type="textarea"
                v-model.trim="formData.businessScope"
                name="businessScope"
                placeholder="请输入经营范围"
                :autosize="{ minRows: 2, maxRows: 4 }"
                style="width: 96.7% !important"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="10">
          <el-col :span="24">
            <el-form-item label="是否区域人员常驻地" prop="ifStation">
              <el-radio-group
                v-model="formData.ifStation"
                @input="ifStationChange"
              >
                <el-radio :label="1">是</el-radio>
                <el-radio v-if="formData.ifStation !== '1'" :label="0"
                  >否</el-radio
                >
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="10">
          <el-col :span="24">
            <el-form-item label="上传公司宣传图" prop="promotionalImage">
              <imageUpload
                :url="formData.promotionalImage"
                @fileUpload="promotionalImageChange"
              />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <div class="bottom">
        <el-button
          type="primary"
          size="small"
          @click="handleFormSubmit"
          v-loading="submitLoading"
          style="float: right"
          >保存
        </el-button>
      </div>
    </el-card>
    <!-- 股权架构 -->
    <el-card class="box-card" shadow="never">
      <template #header>
        <div class="card-header">
          <div class="my_line_box">
            <el-divider direction="vertical" class="my_line"></el-divider>
            <span class="my_line_txt">股权架构</span>
          </div>
        </div>
        <el-button
          type="primary"
          size="small"
          icon="Plus"
          @click="addNewEquity()"
          >新增
        </el-button>
      </template>
      <el-table
        v-loading="tableLoading"
        :cell-style="{ fontSize: '12px', 'text-align': 'center' }"
        :header-cell-style="{ 'text-align': 'center' }"
        :data="equityDataList"
        border
        stripe
      >
        <el-table-column
          type="index"
          label="序号"
          :index="equityTabCount"
          width="60"
        />
        <el-table-column
          label="股东名称"
          prop="shareholderName"
          show-overflow-tooltip
        />
        <el-table-column
          label="占股比例(%)"
          prop="shareProportion"
          show-overflow-tooltip
        />
        <el-table-column label="操作">
          <template #default="{ row }">
            <el-button
              type="primary"
              link
              icon="Edit"
              @click="handleEquityUpdate(row)"
              >编辑
            </el-button>
            <el-button
              type="primary"
              link
              icon="delete"
              @click="handleEquityDelete(row)"
              >删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-show="equityPagination.total > 0"
        :total="equityPagination.total"
        v-model:currentPage="equityPagination.pageNum"
        @update:currentPage="getStructureList"
        layout="->, prev, pager, next"
      />
    </el-card>
    <!-- 组织架构 -->
    <el-card class="box-card" shadow="never">
      <template #header>
        <div class="card-header">
          <div class="my_line_box">
            <el-divider direction="vertical" class="my_line"></el-divider>
            <span class="my_line_txt">组织架构</span>
          </div>
        </div>
      </template>
      <!-- 横向展示 :horizontal="true" -->
      <div class="orgTreeBox">
        <!-- <vue2-org-tree :data="treeData" :renderContent="renderContent" /> -->
        <vue3-tree-org
          :data="treeData"
          center
          :props="treeOrgProps"
          :toolBar="toolBarProps"
          :scalable="false"
        >
          <template v-slot="{ node }">
            <div class="organizational">
              <div class="top">
                <div class="organizational-label">
                  <span>{{
                    node.$$data.lable3
                      ? `${node.$$data.lable1}${node.$$data.lable3}人`
                      : node.$$data.lable1
                  }}</span>
                </div>
                <div class="organizational-name">
                  <span>{{
                    node.$$data.lable2 ? `(${node.$$data.lable2})` : ""
                  }}</span>
                </div>
              </div>
              <div class="organizational-icon">
                <el-icon>
                  <Plus @click.stop="addClick(node)" />
                </el-icon>
                <el-icon>
                  <Edit @click.stop="editClick(node)" />
                </el-icon>
                <el-icon>
                  <Delete @click.stop="deleteClick(node)" />
                </el-icon>
              </div>
            </div>
          </template>
        </vue3-tree-org>
      </div>
    </el-card>
    <!-- 相关证件 -->
    <el-card class="box-card" shadow="never">
      <template #header>
        <div class="my_line_box">
          <el-divider direction="vertical" class="my_line"></el-divider>
          <span class="my_line_txt">相关证件</span>
        </div>
        <el-button
          type="primary"
          size="small"
          icon="Plus"
          @click="addNewDocuments()"
          >新增
        </el-button>
      </template>
      <el-table
        v-loading="tableLoading"
        :cell-style="{ fontSize: '12px', 'text-align': 'center' }"
        :header-cell-style="{ 'text-align': 'center' }"
        :data="documentsDataList"
        border
        stripe
      >
        <el-table-column
          type="index"
          label="序号"
          :index="documentsTabCount"
          width="60"
        />
        <el-table-column label="证件名称" prop="name" />
        <el-table-column label="证件文件" show-overflow-tooltip>
          <template #default="{ row }">
            <el-button type="primary" link @click="certificateDownload(row)">{{
              row.fileName
            }}</el-button>
          </template>
        </el-table-column>
        <el-table-column label="操作">
          <template #default="{ row }">
            <el-button
              type="primary"
              link
              icon="Edit"
              @click="handleDocumentsUpdate(row)"
            >
              编辑
            </el-button>
            <el-button
              type="primary"
              link
              icon="Delete"
              @click="handleDocumentsDelete(row)"
              >删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-show="documentsPagination.total > 0"
        :total="documentsPagination.total"
        v-model:current-page="documentsPagination.pageNum"
        @update:current-page="getCertificateList"
        layout="->, prev, pager, next"
      />
    </el-card>
    <!-- 合同信息 -->
    <el-card class="box-card" shadow="never">
      <template #header>
        <div class="my_line_box">
          <el-divider direction="vertical" class="my_line"></el-divider>
          <span class="my_line_txt">合同信息</span>
        </div>
        <el-button
          class="addButton"
          size="small"
          type="primary"
          icon="Plus"
          @click="addNewContract()"
          >新增
        </el-button>
      </template>
      <el-table
        v-loading="tableLoading"
        :cell-style="{ fontSize: '12px', 'text-align': 'center' }"
        :header-cell-style="{ 'text-align': 'center' }"
        :data="contractDataList"
        border
        stripe
      >
        <el-table-column
          type="index"
          label="序号"
          :index="contractTabCount"
          width="60"
        />
        <el-table-column label="合同名称" prop="name" show-overflow-tooltip />
        <el-table-column
          label="商业模式"
          prop="businessModel"
          show-overflow-tooltip
        />
        <el-table-column label="合同起止日期" show-overflow-tooltip>
          <template #default="{ row }">
            {{ row.effectiveStartTime + "至" + row.effectiveEndTime }}
          </template>
        </el-table-column>
        <el-table-column
          label="合同甲方"
          prop="firstParty"
          show-overflow-tooltip
        />
        <el-table-column
          label="合作单位"
          prop="cooperativeUnit"
          show-overflow-tooltip
        />
        <el-table-column
          label="剩余年限"
          prop="yearsRemaining"
          show-overflow-tooltip
        >
          <template #default="{ row }">
            {{
              setCompareRegistrationTimeText(
                parseTime(new Date(), "{y}-{m}-{d}"),
                row.effectiveEndTime
              )
            }}
          </template>
        </el-table-column>
        <!-- <el-table-column label="关联厂站" align="center" prop="depts" show-overflow-tooltip /> -->
        <!--                <el-table-column label="合同文件" align="center" prop="htwj" show-overflow-tooltip />-->
        <el-table-column label="操作" width="200">
          <template #default="{ row }">
            <el-button
              type="primary"
              size="small"
              link
              icon="Edit"
              @click="handleContractUpdate(row)"
            >
              编辑
            </el-button>
            <el-button
              type="primary"
              size="small"
              link
              icon="Delete"
              @click="handleContractDelete(row)"
              >删除
            </el-button>
            <el-button
              type="primary"
              size="small"
              link
              icon="Download"
              @click="fileDownload(row.fileIdList[0], row.files)"
              v-if="row.files"
              >下载
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-show="contractPagination.total > 0"
        :total="contractPagination.total"
        v-model:current-page="contractPagination.pageNum"
        @update:current-page="getContractDataList"
        layout="->, prev, pager, next"
      />
    </el-card>

    <!--股权新增弹窗-->
    <el-dialog
      :close-on-click-modal="false"
      :title="dialogTitle"
      v-model="openDialogEquity"
      width="30%"
      append-to-body
    >
      <el-form
        ref="equityFormRef"
        :model="equityFormData"
        :rules="equityFormRules"
        id="equityForm"
        label-position="top"
      >
        <el-row :gutter="10">
          <el-col :span="24">
            <el-form-item label="股东名称" prop="shareholderName">
              <el-input
                v-model="equityFormData.shareholderName"
                name="shareholderName"
                placeholder="请输入股东名称"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="10">
          <el-col :span="24">
            <el-form-item label="占股比例(%)" prop="shareProportion">
              <el-input-number
                v-model="equityFormData.shareProportion"
                :max="100"
                name="shareProportion"
                placeholder="请输入占股比例"
                :precision="0"
                :controls="false"
                style="width: 100%"
              >
              </el-input-number>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button size="small" @click="openDialogEquity = false"
            >取消</el-button
          >
          <el-button
            type="primary"
            size="small"
            @click="handleEquityFormSubmit"
            v-loading="submitLoading"
            >确定</el-button
          >
        </div>
      </template>
    </el-dialog>

    <!--证件新增弹窗-->
    <el-dialog
      :close-on-click-modal="false"
      :title="dialogTitle"
      v-model="openDialogDocuments"
      width="30%"
      append-to-body
    >
      <el-form
        ref="documentsFormRef"
        :model="documentsFormData"
        :rules="documentsFormRules"
        id="documentsForm"
        label-position="top"
      >
        <el-row :gutter="10">
          <el-col :span="24">
            <el-form-item label="证件名称" prop="certificateName">
              <el-input
                v-model="documentsFormData.certificateName"
                name="certificateName"
                placeholder="请输入证件名称"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="10">
          <el-col :span="24">
            <el-form-item label="证件文件" prop="certificateInfoList">
              <fileUpload
                :filesObj="documentsFormData.filesObj"
                :size="1"
                @change="fileChange"
              />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button size="small" @click="openDialogDocuments = false"
            >取消</el-button
          >
          <el-button
            type="primary"
            size="small"
            @click="handleDocumentsFormSubmit"
            v-loading="submitLoading"
            >确定
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!--合同新增弹窗-->
    <el-dialog
      :close-on-click-modal="false"
      :title="dialogTitle"
      v-model="openDialogContract"
      width="60%"
      append-to-body
    >
      <el-form
        ref="contractFormRef"
        :model="contractFormData"
        :rules="contractFormRules"
        id="contractForm"
        label-position="top"
      >
        <el-row :gutter="10">
          <el-col :span="8">
            <el-form-item label="合同名称" prop="name">
              <el-input
                v-model="contractFormData.name"
                name="name"
                placeholder="请输入合同名称"
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="商业模式" prop="businessModel">
              <el-input
                v-model="contractFormData.businessModel"
                name="businessModel"
                placeholder="请输入商业模式"
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="合同起止日期" prop="startAndEnd">
              <el-date-picker
                v-model="contractFormData.startAndEnd"
                type="daterange"
                value-format="YYYY-MM-DD HH:mm:ss"
                format="YYYY-MM-DD HH:mm:ss"
                range-separator="-"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                style="width: 100%"
                @change="startAndEndChange"
              >
              </el-date-picker>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="10">
          <el-col :span="8">
            <el-form-item label="合同甲方" prop="firstParty">
              <el-input
                v-model="contractFormData.firstParty"
                name="firstParty"
                placeholder="请输入合同甲方"
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="合作单位" prop="cooperativeUnit">
              <el-input
                v-model="contractFormData.cooperativeUnit"
                name="cooperativeUnit"
                placeholder="请输入合作单位"
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="剩余年限" prop="yearsRemaining">
              <el-input
                v-model="contractFormData.yearsRemaining"
                name="yearsRemaining"
                placeholder="请输入剩余年限"
                disabled
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="10">
          <el-col :span="8">
            <el-form-item label="关联厂站" prop="depts">
              <el-select
                v-model="contractFormData.depts"
                multiple
                placeholder="请选择关联厂站"
                clearable
                style="width: 100%"
              >
                <el-option
                  v-for="item in factoryStationList"
                  :key="item.id"
                  :label="item.organizationName"
                  :value="item.organizationCode"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="合同文件" prop="fileInfo">
              <fileUpload
                :filesObj="contractFormData.contractFilesObj"
                :size="1"
                @change="contractFileChange"
              />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button size="small" @click="openDialogContract = false"
            >取消</el-button
          >
          <el-button
            type="primary"
            size="small"
            @click="handleContractFormSubmit"
            v-loading="submitLoading"
            >确定
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 组织架构新增修改弹窗 -->
    <el-dialog
      :close-on-click-modal="false"
      :title="dialogTitle"
      v-model="openDialogOrganization"
      width="30%"
      append-to-body
    >
      <el-form
        ref="organizationFormRef"
        :model="organizationFormData"
        :rules="organizationFormRules"
        id="organizationForm"
        label-position="top"
      >
        <el-row :gutter="10">
          <el-col :span="24">
            <el-form-item label="组织名称" prop="lable1">
              <el-input
                v-model.trim="organizationFormData.lable1"
                placeholder="请输入组织名称"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="10">
          <el-col :span="24">
            <el-form-item label="负责人" prop="lable2">
              <el-input
                v-model.trim="organizationFormData.lable2"
                placeholder="请输入负责人"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="10">
          <el-col :span="24">
            <el-form-item label="组织人数" prop="lable3">
              <el-input-number
                v-model="organizationFormData.lable3"
                :max="999"
                placeholder="请输入组织人数"
                :precision="0"
                :controls="false"
                style="width: 100%"
              >
              </el-input-number>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button size="small" @click="handleOrganizationFormReset()"
            >取消</el-button
          >
          <el-button
            type="primary"
            size="small"
            @click="handleOrganizationFormSubmit"
            v-loading="submitLoading"
            >确定</el-button
          >
        </div>
      </template>
    </el-dialog>

    <!-- 预览窗口 -->
    <company-preview ref="companyPreviewRef" :deptId="props.params.id" />
  </div>
</template>

<style lang="scss">
.my_line_box {
  display: flex;
  align-items: center;
}
.my_line {
  background-color: #3f78f7;
  width: 4px;
  height: 14px;
}
.my_line_txt {
  font-family: "PingFang SC";
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  text-align: justify;
  line-height: 14px;
}
.org-tree-container {
  overflow: auto;
}

.org-tree-node-label-inner {
  width: 130px;
  height: 53px;
  padding: 0 !important;

  .organizational {
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;

    .top {
      display: flex;
      flex-direction: column;
      width: 100%;
      flex: 3;

      .organizational-label {
        flex: 1;
        width: 100%;
        font-size: 14px;
        font-weight: 600;
        background: #f1f6fe;
      }

      .organizational-name {
        flex: 1;
        width: 100%;
        font-size: 12px;
        background: #f1f6fe;
      }
    }

    .organizational-icon {
      flex: 1;
      width: 100%;
      display: flex;
      justify-content: space-around;
      align-items: center;

      i:hover {
        cursor: pointer;
      }
    }
  }
}
</style>
<style lang="scss" scoped>
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.el-card {
  border: none;
}

.card-title {
  font-size: 14px;
  font-weight: 500;
}

.bottom {
  .button {
    float: right;
  }
}

.addButton {
  margin-bottom: 5px;
}

#equityForm {
  /deep/ .el-input__inner {
    text-align: left;
  }
}

/deep/ .el-card__header {
  display: flex;
  justify-content: space-between;

  .card_head {
    padding: 5px 0;
  }

  .clearfix {
    display: flex;
    align-items: center;
  }
}

.pageRow {
  padding: 0 10px;

  /deep/ .el-form-item__label {
    padding: 4px !important;
    font-family: "PingFang SC";
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    color: #333;
  }

  .el-input {
    width: 90%;
  }

  .el-cascader .el-input {
    width: 90% !important;
  }

  .el-select {
    width: 90% !important;
  }

  .el-date-editor.el-input {
    width: 90% !important;
  }

  .el-textarea {
    width: 90% !important;
  }
}

.orgTreeBox {
  width: 100%;
  height: 400px;
}

.tree-org-node__text {
  text-align: left;
  font-size: 14px;
  .custom-content {
    padding-bottom: 8px;
    margin-bottom: 8px;
    border-bottom: 1px solid currentColor;
  }
}

.organizational {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;

  .top {
    display: flex;
    flex-direction: column;
    width: 100%;
    flex: 3;

    .organizational-label {
      flex: 1;
      width: 100%;
      font-size: 14px;
      font-weight: 600;
      background: #f1f6fe;
    }

    .organizational-name {
      flex: 1;
      width: 100%;
      font-size: 12px;
      background: #f1f6fe;
    }
  }

  .organizational-icon {
    flex: 1;
    width: 100%;
    display: flex;
    justify-content: space-around;
    align-items: center;

    i:hover {
      cursor: pointer;
    }
  }
}
</style>
